import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RightDrawer from '../components/RightDrawer';
import AdminLayout from '../components/AdminLayout';
import { Icon } from '../utils/icons';
import StatCard from '../components/StatCard';
import KpiDelta from '../components/KpiDelta';
import { countBeforeThisMonth } from '../components/kpiMonth';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const STATUS_CONFIG = {
  not_started:     { label: 'Not Started',     color: '#6b7280', bg: '#f3f4f6' },
  started:         { label: 'Started',          color: '#6b7280', bg: '#f3f4f6' },
  submitted:       { label: 'Submitted',        color: '#3a6ad9', bg: '#eff6ff' },
  pre_qualified:   { label: 'Pre-Qualified',    color: '#3a6ad9', bg: '#eff6ff' },
  in_review:       { label: 'In Review',        color: '#ffffff', bg: '#4F46E5' },  // Indigo
  approved:        { label: 'Approved',         color: '#1a1a1a', bg: '#acf44e' },  // Doorly green
  home_shopping:   { label: 'Home Shopping',    color: '#ffffff', bg: '#7C58F5' },  // Violet
  offer_requested: { label: 'Offer Requested',  color: '#1a1a1a', bg: '#FF7A1A' },  // Orange
  option_period:   { label: 'Under Contract',   color: '#1a1a1a', bg: '#FFDD86' },  // Tan
  closing:         { label: 'Closing',          color: '#1a1a1a', bg: '#14B8A6' },  // Teal
  closed:          { label: 'Closed ✓',         color: '#ffffff', bg: '#111827' },  // Dark
  archived:        { label: 'Archived',         color: '#9ca3af', bg: '#f3f4f6' },
  adverse:         { label: 'Adversed',         color: '#ff082f', bg: '#fff0f3' },  // red
  // ── 🚨 A THIRD KIND OF ROW, AND IT IS NOT A BORROWER STATUS ──────────────
  //
  // This page showed only realtor referrals (buyer_referrals). An originator
  // submits a complete application into broker_submissions, and nothing read it
  // — so an LO's borrower was attributed to no partner on the one page that
  // answers "which partner brought this person".
  //
  // ⚠️ IT IS THE STATE OF THE FILE, NOT OF A BORROWER. There is no
  // application_status to report, because the borrower has not confirmed it yet.
  // Giving it a STATUS_CONFIG entry is also what puts it in the filter list:
  // without one the rows still appear (the unknown-status guard below keeps
  // them) but they cannot be filtered FOR, which on a page of 30 referrals is
  // the difference between visible and findable.
  broker_submitted: { label: 'Broker — awaiting borrower', color: '#92400e', bg: '#fffbeb' },
};

const fmt = (v) => v && parseFloat(v) > 0 ? '$' + Math.round(parseFloat(v)).toLocaleString() : '—';

export default function AdminREReferrals() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_active: 0, total_closed: 0, total_closed_volume: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const ALL_STATUSES = Object.keys(STATUS_CONFIG);
  const DEFAULT_STATUSES = ALL_STATUSES.filter(s => s !== 'archived' && s !== 'adverse');
  const [selectedStatuses, setSelectedStatuses] = useState(new Set(DEFAULT_STATUSES));
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  // Hidden by default — which is what the page always claimed to do. The toggle
  // makes the previously-accidental behaviour deliberate and reversible, so a
  // partner's full referral history is still reachable rather than deleted from
  // the view.
  const [showArchived, setShowArchived] = useState(false);
  const [filterPartner, setFilterPartner] = useState('all');
  // 🚨 THE ROW'S `id` IS NOT A BORROWER ID ON HALF THESE ROWS. The list is a
  // union of `users` and `buyer_referrals`, both returning a column called
  // `id`, and the row click sent an unlinked referral to
  // /admin/borrowers/<buyer_referrals.id> — a DIFFERENT PERSON'S FILE. The
  // backend now stamps `source`, and nothing here navigates by id.
  const [detail, setDetail] = useState(null);
  // ⚠️ AND THE DEFAULT ORDER WAS NOT AN ORDER. The endpoint sorts each half of
  // the union separately and then concatenates, so every `users` row sorted
  // newest-first was followed by every `buyer_referrals` row sorted
  // newest-first — two descending runs, read as one list. Sorting here makes
  // the whole merged set one order, which is what the column headers need
  // anyway.
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });

  // ── 🚨 THE TABLE OVERFLOWED ITS CARD AND CUT OFF "View →" (Jesse, 2026-11-02) ─────────────────────────────────
  // Ten columns at minWidth 900 inside a card that is narrower than that at 1280 with the sidebar open, so the action
  // column sat off the right edge behind a scrollbar at the bottom of a long list. Two fixes, both needed:
  //   1. RESPONSIVE COLUMNS — the card's own width (not the window's) decides which secondary columns show; each one
  //      is still on the row's panel, so nothing is lost by hiding it;
  //   2. THE ACTION COLUMN IS STICKY to the card's right edge, so whatever does not fit scrolls UNDER it inside the
  //      card and "View →" is never clipped.
  const cardRef = useRef(null);
  const [cardW, setCardW] = useState(1400);
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;
    const measure = () => setCardW(el.clientWidth || 1400);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Hidden below these card widths (px), least useful first. Every one stays on the row's panel.
  const HIDE_BELOW = { 'Closing Date': 1180, 'Loan #': 1080, 'Partner Type': 980, 'State': 880, 'Date Added': 760 };
  const showCol = (h) => !(HIDE_BELOW[h] && cardW < HIDE_BELOW[h]);
  const COLUMNS = ['Borrower', 'Loan #', 'Status', 'State', 'Purchase Price', 'Referral Partner', 'Partner Type', 'Date Added', 'Closing Date', ''].filter(showCol);
  const STICKY_ACTION = { position: 'sticky', right: 0, background: 'inherit', borderLeft: '1px solid #f3f4f6', zIndex: 1 };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API}/api/admin/partners/referrals`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        console.log('[AdminReferrals] Loaded', data.referrals?.length, 'referrals');
        setReferrals(data.referrals || []);
        setStats({ total: data.total || 0, total_active: data.total_active || 0, total_closed: data.total_closed || 0, total_closed_volume: data.total_closed_volume || 0 });
      })
      .catch(e => console.error('[AdminReferrals] Error:', e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const partnerNames = [...new Set(referrals.map(r => r.referring_partner_name).filter(Boolean))];

  // Column header → the value each row sorts on. A header absent from here is
  // not sortable, which is how the actions column stays inert.
  const SORT_KEYS = {
    'Borrower': (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim().toLowerCase(),
    'Loan #': (r) => (r.loan_number || '').toLowerCase(),
    'Status': (r) => (r.application_status || '').toLowerCase(),
    'State': (r) => (r.buying_state || '').toLowerCase(),
    'Purchase Price': (r) => Number(r.borrower_purchase_price || 0),
    'Referral Partner': (r) => (r.referring_partner_name || '').toLowerCase(),
    'Partner Type': (r) => (r.partner_type || '').toLowerCase(),
    'Date Added': (r) => (r.created_at ? new Date(r.created_at).getTime() : 0),
    'Closing Date': (r) => (r.date_closing ? new Date(r.date_closing).getTime() : 0),
  };
  const HEADER_FOR_KEY = { created_at: 'Date Added' };
  const toggleSort = (h) => {
    if (!SORT_KEYS[h]) return;
    setSort((s0) => (s0.key === h ? { key: h, dir: s0.dir === 'asc' ? 'desc' : 'asc' } : { key: h, dir: 'asc' }));
  };
  // ⚠️ EMPTY VALUES SORT LAST IN BOTH DIRECTIONS. A referral with no closing
  // date is not "earliest"; it has no date. Sorting it to the top of an
  // ascending list would bury the rows the column is being read for.
  const sortKey = HEADER_FOR_KEY[sort.key] || sort.key;
  const sortFn = SORT_KEYS[sortKey] || SORT_KEYS['Date Added'];
  const isEmpty = (v) => v === '' || v === 0 || v === null || v === undefined;

  const filtered = referrals.filter(r => {
    // ⚠️ ARCHIVED/ADVERSED ARE BOOLEANS, NOT STATUSES — AND THIS FILTER USED TO
    // TEST THEM AS STATUSES. It matched application_status against 'archived'
    // and 'adverse', and NO borrower ever has those values: archived-ness lives
    // in is_archived / is_adversed, the same two columns the borrower pipeline
    // reads. So the default "hide archived" never filtered on archived-ness at
    // all. It only LOOKED right because most archived borrowers sit at
    // 'not_moving_forward', which has no STATUS_CONFIG entry and so fell through
    // the unknown-status branch below. Nadia Kobayashi is archived at
    // 'not_started' — a normal in-funnel status — so nothing hid her, and every
    // future archived borrower at a normal status would have appeared too.
    if (!showArchived && (r.is_archived || r.is_adversed)) return false;

    // ⚠️ AN UNKNOWN STATUS MUST NOT VANISH. This dropped any row whose status was
    // absent from STATUS_CONFIG, which is not a filter anyone asked for — it was
    // hiding ACTIVE referrals as collateral. David Ijoni Animadu sits at
    // 'in_progress', has no config entry, is not archived, and was being removed
    // from the list for no reason. The row renderer already falls back to the raw
    // status label, so an unrecognised value renders fine; only this predicate
    // was throwing it away first.
    const known = ALL_STATUSES.includes(r.application_status);
    if (known && selectedStatuses.size < ALL_STATUSES.length && !selectedStatuses.has(r.application_status)) return false;
    if (filterPartner !== 'all' && r.referring_partner_name !== filterPartner) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const name = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
      if (!name.includes(q) && !(r.email || '').toLowerCase().includes(q) && !(r.loan_number || '').toLowerCase().includes(q) && !(r.referring_partner_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: '#111827' }}>Partner Referrals</h1>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>All borrowers referred through the DoorlyPro partner program</p>
        </div>

        {/* ── KPIs — StatCard, like Partners and Accounts ──────────────────
            🚨 THESE WERE HAND-ROLLED DIVS: tinted backgrounds, a coloured value at
            28px/900 against StatCard's 26px/700, a different border and a static
            caption. Three pages on one tab, two of them sharing a component and
            this one not — mirror drift in CSS, which is the same failure this
            codebase has hit in program terms, partner statuses and the FICO
            ladder. The numbers were fine; the rendering was a second
            implementation nobody had to keep in step. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <StatCard label="Total Referrals" value={stats.total} bg="#eff6ff" color="#3a6ad9"
            icon={<Icon name="users" size={18} color="#3a6ad9" />}>
            <KpiDelta value={stats.total} prev={countBeforeThisMonth(referrals)} />
            {/* 🚨 `total` NOW MEANS ALL-TIME. It was rows.filter(!isInactive) —
                archived, adversed and dead-status referrals were silently dropped
                — so a card labelled "Total Referrals" undercounted what had ever
                been submitted, while the line directly beneath it already gave the
                active figure. Two numbers that differed only by an invisible
                filter read as one number and a typo. */}
            <div style={{ marginTop: 2, fontSize: 10, color: '#9ca3af' }}>{stats.total_active} active</div>
          </StatCard>
          <StatCard label="Closed Loans" value={stats.total_closed} bg="#f4fee8" color="#2d6a0a"
            icon={<Icon name="check-circle" size={18} color="#2d6a0a" />}>
            <KpiDelta note="Successfully closed" />
          </StatCard>
          {/* ⚠️ NO DELTA ON VOLUME, AND NOT BECAUSE IT IS HARD. created_at on a
              referral is when it was SUBMITTED, not when it closed — so a
              "vs last month" built from it would compare money against the wrong
              date entirely. A figure with no honest baseline gets a note. */}
          <StatCard label="Total Closed Volume" bg="#f4fee8" color="#2d6a0a"
            value={stats.total_closed_volume > 0
              ? '$' + (stats.total_closed_volume >= 1e6
                ? (stats.total_closed_volume / 1e6).toFixed(1) + 'M'
                : Math.round(stats.total_closed_volume / 1e3) + 'K')
              : '$0'}
            icon={<Icon name="currency" size={18} color="#2d6a0a" />}>
            <KpiDelta note="Combined purchase price" />
          </StatCard>
        </div>

        {/* Filters */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px 18px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}><Icon name="search" size={14} color="#9ca3af" /></span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, loan #, or partner..."
              style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }} />
          </div>
          <div style={{ position: 'relative' }}>
            <button type="button" onClick={() => setStatusDropdownOpen(v => !v)}
              style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', minWidth: '140px' }}>
              {selectedStatuses.size === ALL_STATUSES.length ? 'All Statuses' : selectedStatuses.size === 0 ? 'No Statuses' : `${selectedStatuses.size} Statuses`}
              <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: 'auto' }}>▾</span>
            </button>
            {statusDropdownOpen && (
              <>
                <div onClick={() => setStatusDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 98 }} />
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', zIndex: 99, minWidth: '200px', maxHeight: '320px', overflowY: 'auto', padding: '6px 0' }}>
                  <div style={{ padding: '4px 12px', display: 'flex', gap: '8px', borderBottom: '1px solid #f3f4f6', marginBottom: '4px' }}>
                    <button type="button" onClick={() => setSelectedStatuses(new Set(ALL_STATUSES))} style={{ fontSize: '11px', color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', padding: '2px 0' }}>All</button>
                    <button type="button" onClick={() => setSelectedStatuses(new Set())} style={{ fontSize: '11px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', padding: '2px 0' }}>None</button>
                    <button type="button" onClick={() => setSelectedStatuses(new Set(DEFAULT_STATUSES))} style={{ fontSize: '11px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', padding: '2px 0' }}>Default</button>
                  </div>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 12px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <input type="checkbox" checked={selectedStatuses.has(k)} onChange={() => {
                        setSelectedStatuses(prev => { const next = new Set(prev); if (next.has(k)) next.delete(k); else next.add(k); return next; });
                      }} style={{ accentColor: '#3a6ad9' }} />
                      <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: v.color, flexShrink: 0 }} />
                      {v.label}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* ⚠️ THE TOGGLE EXISTS SO "HIDDEN" IS A CHOICE, NOT A DISAPPEARANCE. The
              page hid archived borrowers by accident before; hiding them on
              purpose with no way back would lose a partner's referral history
              from the one view that shows it. Off by default, matching what the
              page always claimed to do. */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6b7280', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)}
              style={{ accentColor: '#3a6ad9', cursor: 'pointer' }} />
            Show archived &amp; adversed
          </label>
          {partnerNames.length > 1 && (
            <select value={filterPartner} onChange={e => setFilterPartner(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', background: '#fff', outline: 'none', cursor: 'pointer' }}>
              <option value="all">All Partners</option>
              {partnerNames.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          {(search || selectedStatuses.size !== DEFAULT_STATUSES.length || filterPartner !== 'all') && (
            <button onClick={() => { setSearch(''); setSelectedStatuses(new Set(DEFAULT_STATUSES)); setFilterPartner('all'); }}
              style={{ padding: '7px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#374151' }}>✕ Clear</button>
          )}
          <span style={{ fontSize: '13px', color: '#6b7280', marginLeft: 'auto' }}><strong>{filtered.length}</strong> of {referrals.length}</span>
        </div>

        {/* Table */}
        <div ref={cardRef} data-testid="referrals-table-card" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Loading referrals...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ marginBottom: '12px' }}><Icon name="users" size={40} color="#d1d5db" /></div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{referrals.length === 0 ? 'No referrals yet' : 'No referrals match your filters'}</div>
              <div style={{ fontSize: '13px' }}>{referrals.length === 0 ? 'Borrowers invited by DoorlyPro partners will appear here' : 'Try adjusting your filters'}</div>
            </div>
          ) : (
            <div data-testid="referrals-table-scroll" style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    {COLUMNS.map(h => {
                      const sortable = !!SORT_KEYS[h];
                      const active = sortable && sortKey === h;
                      return (
                        <th key={h} onClick={() => toggleSort(h)}
                          title={sortable ? `Sort by ${h}` : undefined}
                          style={{ padding: '11px 14px', textAlign: 'left', fontSize: '11px',
                            color: active ? '#3a6ad9' : '#6b7280', fontWeight: '600', textTransform: 'uppercase',
                            letterSpacing: '0.04em', whiteSpace: 'nowrap',
                            cursor: sortable ? 'pointer' : 'default', userSelect: 'none',
                            ...(h === '' ? STICKY_ACTION : {}) }}>
                          {h}
                          {/* The caret marks the ACTIVE column only. Showing a
                              neutral glyph on every sortable header turns the
                              row into noise and hides which one is in effect. */}
                          {active && <span style={{ marginLeft: 4 }}>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {[...filtered].sort((a, b) => {
                    const av = sortFn(a), bv = sortFn(b);
                    if (isEmpty(av) !== isEmpty(bv)) return isEmpty(av) ? 1 : -1;
                    if (av === bv) return 0;
                    return (av > bv ? 1 : -1) * (sort.dir === 'asc' ? 1 : -1);
                  }).map(r => {
                    // ⚠️ THE FLAG OUTRANKS THE STATUS IN THE PILL. An archived
                    // borrower still carries whatever status they had when they
                    // were archived — Nadia reads 'not_started' — so showing the
                    // raw status would repeat the exact confusion that made this
                    // list disagree with the borrower pipeline. When a row is
                    // only visible because the toggle is on, say WHY it is here.
                    const sc = r.is_adversed ? STATUS_CONFIG.adverse
                      : r.is_archived ? STATUS_CONFIG.archived
                      : (STATUS_CONFIG[r.application_status] || { label: r.application_status || '—', color: '#6b7280', bg: '#f3f4f6' });
                    const name = `${r.first_name || ''} ${r.last_name || ''}`.trim() || '—';
                    return (
                      <tr key={`${r.source || 'user'}-${r.id}`} data-testid="referral-row" data-source={r.source || 'user'}
                        style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: '#fff' }}
                        onClick={() => setDetail(r)}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{name}</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{r.email}</div>
                        </td>
                        {showCol('Loan #') && <td style={{ padding: '12px 14px', fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>{r.loan_number || '—'}</td>}
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '10px', background: sc.bg, color: sc.color, border: `1px solid ${sc.color}25`, whiteSpace: 'nowrap' }}>{sc.label}</span>
                        </td>
                        {showCol('State') && <td style={{ padding: '12px 14px', fontSize: '13px', color: '#374151' }}>{r.buying_state || '—'}</td>}
                        <td style={{ padding: '12px 14px', fontSize: '13px', fontWeight: '600', color: '#111827' }}>{r.borrower_purchase_price ? fmt(r.borrower_purchase_price) : <span style={{ color: '#9ca3af' }}>Not yet priced</span>}</td>
                        <td style={{ padding: '12px 14px' }}>
                          {r.referring_partner_name ? (
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#3a6ad9' }}>{r.referring_partner_name}</div>
                              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{r.referring_partner_email}</div>
                            </div>
                          ) : <span style={{ color: '#d1d5db' }}>—</span>}
                        </td>
                        {showCol('Partner Type') && <td style={{ padding: '12px 14px' }}>
                          {r.partner_type ? (
                            <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '8px',
                              background: r.is_homebuilder ? '#fff7ed' : r.partner_type === 'loan_officer' ? '#eff6ff' : '#f4fee8',
                              color: r.is_homebuilder ? '#7a5200' : r.partner_type === 'loan_officer' ? '#2854c0' : '#2d6a0a' }}>
                              {r.is_homebuilder ? 'Builder' : r.partner_type === 'loan_officer' ? 'LO' : 'Realtor'}
                            </span>
                          ) : <span style={{ color: '#d1d5db', fontSize: '12px' }}>—</span>}
                        </td>}
                        {showCol('Date Added') && <td style={{ padding: '12px 14px', fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>}
                        {showCol('Closing Date') && <td style={{ padding: '12px 14px', fontSize: '12px', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                          {r.date_closing ? new Date(r.date_closing + (String(r.date_closing).length === 10 ? 'T12:00:00' : '')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>}
                        <td style={{ padding: '12px 14px', ...STICKY_ACTION }}>
                          <button data-testid="referral-view" onClick={e => { e.stopPropagation(); setDetail(r); }}
                            style={{ padding: '4px 10px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: '#374151', fontWeight: '600', whiteSpace: 'nowrap' }}>
                            View →
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── 🚨 A PANEL, NOT A NAVIGATION ────────────────────────────────────
          The row click used to go to /admin/borrowers/<r.id>, and on an
          unlinked referral that id is a buyer_referrals id — a different
          person's borrower file. It also required the Borrowers tab, which
          partner-success roles do not have, so for them the button did
          nothing reachable.

          ⚠️ EVERYTHING HERE IS ALREADY ON THE ROW. No second fetch, so the
          panel cannot show a partner something the list was not already
          cleared to return — the endpoint's PARTNER_HIDDEN projection
          (max_approval_amount, doorly_purchase_price, resale_adjustment)
          governs both, because there is only one payload. */}
      {/* 🔑 THE SHARED PANEL. This was a hand-rolled 420px drawer at z-60 —
          under the navbar (1000) and the assistant launcher (999), so its top
          64px and its bottom-right corner were covered. components/RightDrawer
          carries that fix, the fixed header, Escape-to-close and the dvh height
          that the four copies each had some of. */}
      <RightDrawer
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? (`${detail.first_name || ''} ${detail.last_name || ''}`.trim() || '—') : ''}
        /* 🔑 SAYS WHICH KIND OF RECORD THIS IS. "Invited, not yet registered" is
           the honest description of a buyer_referrals row, and it is why there is
           no loan number to show. */
        subtitle={detail ? (detail.source === 'referral' ? 'Invited — not yet registered'
          : detail.source === 'broker_submission' ? 'Broker submission — awaiting borrower registration'
            : 'Registered borrower') : ''}
      >
        {detail && (<>

            <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
              {[
                ['Status', (detail.is_adversed ? 'Adversed'
                  : detail.is_archived ? 'Archived'
                  : (STATUS_CONFIG[detail.application_status]?.label || detail.application_status || '—'))],
                ['Email', detail.email || '—'],
                ['Phone', detail.phone || '—'],
                ['State', detail.buying_state || '—'],
                ['Loan #', detail.loan_number || '—'],
                ['Purchase price', detail.borrower_purchase_price ? fmt(detail.borrower_purchase_price) : 'Not yet priced'],
                ['Referred by', detail.referring_partner_name || '—'],
                ['Partner email', detail.referring_partner_email || '—'],
                ['Referred on', detail.created_at ? new Date(detail.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'],
                ['Closing date', detail.date_closing ? new Date(detail.date_closing + (String(detail.date_closing).length === 10 ? 'T12:00:00' : '')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 14,
                  borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                  <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>{k}</span>
                  <span style={{ fontSize: 13, color: '#111827', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
                </div>
              ))}
            </div>

            {/* ⚠️ THE ONLY LINK OUT, AND ONLY WHERE THE ID MEANS WHAT THE URL
                SAYS. A referral row has no borrower file to open, so it gets no
                button rather than a button that opens somebody else's. */}
            {/* ── 🚨 IT WAS A DEAD BUTTON FOR THE ROLE THAT SEES THIS PAGE ──────
                Jesse: "'Open Borrower File' should actually open the file and be
                a working link, not a dead button."

                🔑 TWO THINGS WERE WRONG AND ONLY ONE WAS VISIBLE. The href
                pointed at a real route, but /admin/borrowers/:id was refused for
                partner_success by the nav rule — so the one role whose whole job
                is this page got the "not part of your role" explainer. That half
                is fixed in constants/staffNav.js and the server now scopes the
                file by referral.

                ⚠️ AND IT WAS AN <a href>, WHICH IS A FULL PAGE LOAD. Inside the
                DoorlyDesk PWA that drops the standalone context on iOS and
                reloads the whole bundle; a router navigation keeps both. */}
            {/* 🚨 A BROKER SUBMISSION'S id IS NOT A BORROWER id (Jesse, 2026-11-02). "Open borrower file" on submission
                #11 navigated to /admin/borrowers/11 — a blank "Borrower not found" (or somebody else's file). It opens
                the submission record now, which forwards to the borrower file once they register. */}
            {detail.source === 'broker_submission' && (
              <button type="button" data-testid="referral-open-submission"
                onClick={() => navigate(`/admin/partners/submissions/${detail.id}`)}
                style={{ display: 'inline-block', marginTop: 18, padding: '8px 14px', borderRadius: 8,
                  background: '#3a6ad9', color: '#fff', fontSize: 13, fontWeight: 600,
                  border: 'none', cursor: 'pointer' }}>
                Open submission →
              </button>
            )}
            {detail.source === 'user' && (
              <button type="button" data-testid="referral-open-borrower" onClick={() => navigate(`/admin/borrowers/${detail.id}`)}
                style={{ display: 'inline-block', marginTop: 18, padding: '8px 14px', borderRadius: 8,
                  background: '#3a6ad9', color: '#fff', fontSize: 13, fontWeight: 600,
                  border: 'none', cursor: 'pointer' }}>
                Open borrower file →
              </button>
            )}
        </>)}
      </RightDrawer>
    </AdminLayout>
  );
}
